import EmissionsReportForm from '../components/supplierEmissionsReportForm/SupplierEmissionsReportForm'
import {
  SupplierEmissionsReportFormData,
  validateSupplierEmissionsReportForm
} from '../components/supplierEmissionsReportForm/SupplierEmissionsReportForm.schema'
import useI18n from '@/shared/hooks/usei18n'
import { GenericFullscreenDialog } from '@/common/components/FullscreenDialog/GenericFullscreenDialog'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Installation } from '@/features/suppliers/hooks/installations'
import { Button } from '@/common/components/Button/Button'
import gridEmissionFactorByCountry from '@/common/config/gridEmissionFactorByCountry'
import {
  SupplierEmissionsReport,
  useUpdateSupplierEmissionsReport
} from '@/features/suppliers/hooks/supplierEmissionsReport'
import toast from 'react-hot-toast'
import { ElectricityProductionMethod, productionMethodsByCode } from '@/common'
import { getElectricityEmissionFactor } from '@/shared/utils/getElectricityEmissionFactor'
import { SupplierEmissionsReportIncompletePeriodCallout } from '../components/SupplierEmissionsReportIncompletePeriodCallout'
import { SupplierEmissionsReportLinkedGoodsCallout } from '../components/SupplierEmissionsReportLinkedGoodsCallout'

export default function SupplierEmissionsReportEditDialog({
  isOpen,
  close,
  partnershipId,
  installation,
  supplierEmissionsReports,
  reportToEdit
}: {
  isOpen: boolean
  close: () => void
  partnershipId: string
  installation: Installation
  supplierEmissionsReports: SupplierEmissionsReport[]
  reportToEdit: SupplierEmissionsReport
}) {
  const { t } = useI18n()

  const { mutateAsync: updateSupplierEmissionsReport, isPending: isSaving } =
    useUpdateSupplierEmissionsReport(partnershipId, installation.id)

  const defaultElectricityEmissionFactor =
    gridEmissionFactorByCountry[installation.country as keyof typeof gridEmissionFactorByCountry] ||
    0

  const [formData, setFormData] = useState<SupplierEmissionsReportFormData>({
    periodId: '',
    cnCode: '',
    aggregatedGoods: '',
    productionMethod: '',
    goodsDescription: '',
    specificDirectEmbeddedEmissions: '',
    specificIndirectEmbeddedEmissions: '',
    electricityConsumptionMWh: '',
    electricityEmissionFactor: defaultElectricityEmissionFactor.toString(),
    electricityProductionMethod: ElectricityProductionMethod.GRID,
    provideSIEEDirectly: false,
    evidenceFileName: '',
    evidenceType: '',
    startDate: '',
    endDate: ''
  })

  const resetFormData = useCallback(() => {
    if (reportToEdit) {
      const electricityEmissionFactor = getElectricityEmissionFactor(
        Number(reportToEdit.specificIndirectEmbeddedEmissions),
        Number(reportToEdit.electricityConsumptionMWh)
      )

      setFormData({
        periodId: reportToEdit.periodId,
        cnCode: reportToEdit.goods[0]?.cnCode || '',
        aggregatedGoods:
          productionMethodsByCode[reportToEdit.goods[0]?.productionMethodCode]
            ?.aggregatedGoodsCategory || '',
        productionMethod: reportToEdit.goods[0]?.productionMethodCode || '',
        goodsDescription: reportToEdit.goods[0]?.goodsDescription || '',
        specificDirectEmbeddedEmissions: reportToEdit.specificDirectEmbeddedEmissions.toString(),
        specificIndirectEmbeddedEmissions:
          reportToEdit.specificIndirectEmbeddedEmissions.toString(),
        electricityConsumptionMWh: reportToEdit.electricityConsumptionMWh.toString(),
        electricityEmissionFactor: electricityEmissionFactor.toString(),
        electricityProductionMethod: reportToEdit.electricityProductionMethod,
        provideSIEEDirectly:
          reportToEdit.electricityProductionMethod === ElectricityProductionMethod.GRID,
        evidenceFileName: reportToEdit.evidenceFileName || '',
        evidenceType: reportToEdit.evidenceType || '',
        startDate: '',
        endDate: ''
      })
    }
  }, [reportToEdit, defaultElectricityEmissionFactor])

  useEffect(() => {
    resetFormData()
  }, [resetFormData])

  const validationResult = useCallback(() => {
    return validateSupplierEmissionsReportForm(formData, {
      isEditMode: true,
      originalPeriodId: reportToEdit.periodId
    })
  }, [formData, reportToEdit.periodId])

  const onClose = useCallback(() => {
    // Reset form data to original report values when closing
    resetFormData()
    close()
  }, [close, resetFormData])

  const linkedImportedGoodsCount = reportToEdit.linkedImportedGoodsCount ?? 0
  const goodsDataLocked = linkedImportedGoodsCount > 0

  const onSave = useCallback(async () => {
    try {
      const validation = validationResult()
      if (!validation.success) {
        return
      }
      await updateSupplierEmissionsReport({
        reportId: reportToEdit.id,
        payload: validation.data
      })
      toast.success(
        t(
          'supplierEmissionsReport:editSupplierEmissionsReportDialog.notifications.supplierEmissionsReportUpdatedSuccessfully'
        ),
        {
          position: 'top-right'
        }
      )
      onClose()
    } catch (error: unknown) {
      const errorCode = (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message

      if (errorCode === 'SUPPLIER_EMISSIONS_REPORT_LINKED_GOODS_DATA_LOCKED') {
        toast.error(
          t(
            'supplierEmissionsReport:editSupplierEmissionsReportDialog.errors.supplierEmissionsReportLinkedGoodsDataLocked'
          ),
          {
            position: 'top-right'
          }
        )
        return
      }

      console.error(error)
      toast.error(
        t(
          'supplierEmissionsReport:editSupplierEmissionsReportDialog.notifications.failedToUpdateSupplierEmissionsReport'
        ),
        {
          position: 'top-right'
        }
      )
    }
  }, [updateSupplierEmissionsReport, validationResult, t, onClose, reportToEdit.id])

  const isSaveDisabled = useMemo(() => {
    return isSaving || !validationResult().success
  }, [isSaving, validationResult])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={onClose}
      title={t('supplierEmissionsReport:editSupplierEmissionsReportDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('supplierEmissionsReport:editSupplierEmissionsReportDialog.actions.save')}
          </Button>
        </div>
      }
    >
      <SupplierEmissionsReportIncompletePeriodCallout />
      {goodsDataLocked && <SupplierEmissionsReportLinkedGoodsCallout />}
      <EmissionsReportForm
        data={formData}
        onChange={setFormData}
        installation={installation}
        supplierEmissionsReports={supplierEmissionsReports}
        isEditMode
        originalPeriodId={reportToEdit.periodId}
        goodsDataLocked={goodsDataLocked}
      />
    </GenericFullscreenDialog>
  )
}
